import React, { useEffect } from 'react';
import type { OtpInputProps } from './types';
import { useOtpInput } from './useOtpInput';

const BOX_SIZE = {
  default: {
    width: 56,
    height: 56,
    borderRadius: 8,
    gap: 8,
    padding: 0,
  },
  large: {
    width: 87.33,
    height: 56,
    borderRadius: 4,
    gap: 4,
    padding: '4px 16px',
  },
} as const;

const OtpInput: React.FC<OtpInputProps> = ({
  length = 6,
  value,
  onChange,
  onComplete,
  disabled = false,
  error = false,
  errorMessage,
  supportingText,
  className = '',
  autoFocus = false,
  size = 'default',
}) => {
  const {
    digits,
    inputRefs,
    handleChange,
    handleKeyDown,
    handlePaste,
    handleFocus,
    focusedIndex,
  } = useOtpInput({
    length,
    value,
    onChange,
    onComplete,
    disabled,
  });

  // Auto focus first input on mount
  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus, inputRefs]);

  const hasError = error || !!errorMessage;
  const boxStyle = BOX_SIZE[size];

  return (
    <div className={className} style={{ fontFamily: 'Roboto, sans-serif' }}>
      {/* OTP Inputs Container */}
      <div className="flex" style={{ gap: boxStyle.gap }}>
        {digits.map((digit, index) => (
          <div
            key={index}
            className={`
              relative flex items-center justify-center
              transition-all duration-150
              ${disabled ? 'cursor-not-allowed opacity-60 bg-neutral-90' : 'bg-neutral-90 hover:bg-neutral-85'}
            `}
            style={{
              width: boxStyle.width,
              height: boxStyle.height,
              borderRadius: boxStyle.borderRadius,
              padding: boxStyle.padding,
              borderBottomWidth: hasError ? 2 : 1,
              borderBottomStyle: 'solid',
              borderBottomColor: hasError
                ? '#e11b1b'
                : focusedIndex === index
                  ? '#2a4455'
                  : '#6a7278',
            }}
          >
            <input
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              onFocus={() => handleFocus(index)}
              onBlur={() => handleFocus(-1)}
              disabled={disabled}
              className={`
                w-full h-full bg-transparent outline-none text-center
                text-bodyLarge
                ${disabled ? 'cursor-not-allowed text-neutral-60' : 'text-neutral-10'}
              `}
              style={{
                caretColor: hasError ? '#e11b1b' : undefined,
              }}
              aria-label={`OTP digit ${index + 1}`}
              data-testid={`otp-input-${index + 1}`}
              id={`otp-input-${index + 1}`}
            />
          </div>
        ))}
      </div>

      {/* Supporting Text / Error Message */}
      {(supportingText || errorMessage) && (
        <p
          className={`
            mt-2
            text-xs leading-4
            ${hasError ? 'text-error-40' : 'text-neutral-50'}
            ${disabled ? 'text-neutral-60' : ''}
          `}
          style={{ fontSize: 12, lineHeight: '16px' }}
        >
          {errorMessage || supportingText}
        </p>
      )}
    </div>
  );
};

export default OtpInput;
